import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { COLORS } from '../config/colors';
import { FONTS } from '../config/fonts';
import { useIsHorizontal } from '../hooks/useIsHorizontal';
import { strings } from '../strings';
import type {
  PollutantChange,
  PollutantReading,
  Station,
} from '../types/station';
import { getStationPollutants } from '../utils/aqi';

type StationCardProps = {
  station: Station;
};

// How a pollutant value moved relative to its previous reading. An increased
// pollutant means worse air (red), a decreased one means better air (green)
// and an unchanged value is shown in neutral gray.
type ChangeIndicator = {
  symbol: string;
  label: string;
  color: string;
};

const changeIndicators: Record<PollutantChange, ChangeIndicator> = {
  increased: { symbol: '▲', label: strings.increased, color: '#dc2626' },
  decreased: { symbol: '▼', label: strings.decreased, color: '#16a34a' },
  unchanged: { symbol: '—', label: strings.unchanged, color: '#9ca3af' },
};

type MetricBoxProps = {
  label: string;
  reading: PollutantReading;
};

const MetricBox = ({ label, reading }: MetricBoxProps) => {
  const indicator = changeIndicators[reading.change];

  return (
    <View
      accessible
      accessibilityLabel={`${label} ${reading.value} ${indicator.label}`}
      style={styles.metric}
    >
      <Text style={styles.metricLabel}>{label}</Text>
      <Text style={styles.metricValue}>{reading.value}</Text>
      <Text style={[styles.metricChange, { color: indicator.color }]}>
        {indicator.symbol} {indicator.label}
      </Text>
    </View>
  );
};

const StationCard = ({ station }: StationCardProps) => {
  const insets = useSafeAreaInsets();
  const isHorizontal = useIsHorizontal();

  // Only the metrics this station actually reports, in a fixed order.
  const readings = getStationPollutants(station);

  return (
    // Match the bottom distance of the SearchBar: same safe-area inset + 12.
    <View
      style={[
        styles.positioner,
        isHorizontal ? styles.positionerHorizontal : styles.positionerVertical,
        { bottom: insets.bottom + 12 },
      ]}
    >
      <View accessibilityLiveRegion="polite" style={styles.card}>
        <Text accessibilityRole="header" style={styles.name}>
          {station.name}
        </Text>
        <Text style={styles.city}>{station.city}</Text>
        <View style={styles.metrics}>
          {readings.map(({ key, label, reading }) => (
            <MetricBox key={key} label={label} reading={reading} />
          ))}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  positioner: {
    position: 'absolute',
    alignItems: 'center',
  },
  positionerVertical: {
    left: 0,
    right: 0,
  },
  // Horizontal mode docks the card to the left, mirroring the SearchBar.
  positionerHorizontal: {
    left: 16,
    width: '45%',
    maxWidth: 420,
    alignItems: 'flex-start',
  },
  // Same look as the floating buttons/SearchBar, adapted to a card.
  card: {
    direction: 'rtl',
    width: '95%',
    maxWidth: 500,
    backgroundColor: COLORS.surface,
    borderRadius: 15,
    padding: 16,
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 4,
  },
  name: {
    fontFamily: FONTS.regular,
    fontSize: 22,
    color: COLORS.ink,
  },
  city: {
    fontFamily: FONTS.regular,
    fontSize: 14,
    color: COLORS.ink,
  },
  metrics: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 16,
  },
  metric: {
    minWidth: 72,
    flexGrow: 1,
    flexBasis: 0,
    backgroundColor: '#ffffff50',
    borderRadius: 12,
    padding: 12,
    alignItems: 'center',
  },
  metricLabel: {
    fontFamily: FONTS.regular,
    fontSize: 12,
    color: '#6b7280',
  },
  metricValue: {
    fontFamily: FONTS.regular,
    fontSize: 22,
    fontWeight: '700',
    color: COLORS.ink,
    marginTop: 4,
  },
  metricChange: {
    fontFamily: FONTS.regular,
    fontSize: 10,
    fontWeight: '700',
    color: '#9ca3af',
    marginTop: 4,
  },
});

export default StationCard;
